import { useMemo, useRef, useState } from "react";
import {
  legacyPushResultOffices,
  legacySmsAutoSettings,
} from "../../mock/legacySms";
import "./LegacySmsAutoSettingForm.css";

type Mode = "add" | "update";
type ConditionMode = "none" | "include" | "exclude";

const dateOptions = [
  "-- 선택 --",
  "출생일 or 예정일",
  "출생일",
  "출산예정일",
  "돌잔치",
  "엄마생일",
  "아빠생일",
  "결혼기념일",
  "촬영일",
  "계약일",
  "고객등록일",
];
const conditionOptions = [
  "-- 선택 --",
  "고객구분",
  "유입경로",
  "산부인과",
  "조리원",
  "기타경로1",
  "기타경로2",
  "촬영 스케줄",
  "촬영 스케줄 상태",
  "총액(계약)",
  "총액(계약) 마이너스",
  "잔금",
  "계약 패키지 상품",
  "계약 패키지 통계구분",
  "계약 상세상품",
  "계약 상세상품 통계구분",
  "촬영 상품",
];
const mockCodes = ["[Mock 고객명]", "[Mock 촬영일]", "[Mock 담당자]"];

function smsBytes(value: string) {
  return Array.from(value).reduce(
    (sum, char) => sum + (char.charCodeAt(0) > 127 ? 2 : 1),
    0,
  );
}

export function LegacySmsAutoSettingForm({ mode }: { mode: Mode }) {
  const editing = mode === "update";
  const source = editing ? legacySmsAutoSettings[0] : undefined;
  const [office, setOffice] = useState(
    source?.office ?? legacyPushResultOffices[0],
  );
  const [target, setTarget] = useState(source?.target ?? "고객");
  const [dateType, setDateType] = useState(source ? "출생일" : "");
  const [period, setPeriod] = useState(source ? "1" : "0");
  const [periodUnit, setPeriodUnit] = useState("일");
  const [periodOrder, setPeriodOrder] = useState("전");
  const [conditions, setConditions] = useState<
    { mode: ConditionMode; value: string }[]
  >([
    { mode: "include", value: source?.condition1 === "-" ? "" : "고객구분" },
    { mode: "none", value: "" },
    { mode: "none", value: "" },
  ]);
  const [advertising, setAdvertising] = useState(
    source?.advertisement ?? false,
  );
  const [content, setContent] = useState(source?.content ?? "");
  const [isMms, setIsMms] = useState(false);
  const [receiver, setReceiver] = useState("엄마 또는 아빠");
  const [calling, setCalling] = useState(source?.calling ?? "010-0000-0001");
  const [codeOpen, setCodeOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmSave, setConfirmSave] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const byteCount = useMemo(() => smsBytes(content), [content]);
  const messageType = isMms ? "MMS" : byteCount > 90 ? "LMS" : "SMS";
  const updateCondition = (
    index: number,
    value: Partial<{ mode: ConditionMode; value: string }>,
  ) =>
    setConditions((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...value } : item,
      ),
    );
  const addCode = (code: string) => {
    const element = textarea.current;
    const start = element?.selectionStart ?? content.length;
    const end = element?.selectionEnd ?? content.length;
    setContent(`${content.slice(0, start)}${code}${content.slice(end)}`);
    requestAnimationFrame(() => {
      textarea.current?.focus();
      textarea.current?.setSelectionRange(
        start + code.length,
        start + code.length,
      );
    });
  };
  const updateAdvertising = (checked: boolean) => {
    setAdvertising(checked);
    setContent((current) =>
      checked && !current.startsWith("(광고)")
        ? `(광고)${current}`
        : current.replace(/^\(광고\)\s?/, ""),
    );
  };
  const requestSave = (event: React.FormEvent) => {
    event.preventDefault();
    if (!dateType || !content.trim()) {
      setNotice("Mock 필수 항목을 입력해 주세요.");
      return;
    }
    if (advertising && !content.startsWith("(광고)")) {
      setNotice("Mock 광고 문자는 '(광고)'로 시작해야 합니다.");
      return;
    }
    if (isMms) {
      setNotice("Mock MMS는 파일 업로드 없이 샘플 상태로만 저장됩니다.");
      return;
    }
    setConfirmSave(true);
  };
  const completeSave = () => {
    setConfirmSave(false);
    setNotice(
      `Mock 자동 발송 조건이 ${editing ? "수정" : "저장"}되었습니다. 실제 문자·PUSH·API 호출은 발생하지 않았습니다.`,
    );
  };
  return (
    <main className="legacy-m06-popup-main">
      <div className="legacy-m06-popup-content">
        <h1 className="g_program_title">
          문자 자동 발송 설정 {editing ? "수정" : "추가"}
        </h1>
        <section className="g_card legacy-m06-popup-card">
          <form
            id="l_form_pmsg_auto_send_setting"
            className="g_form legacy-m06-popup-form"
            onSubmit={requestSave}
          >
            <PopupRow title="지점" required>
              <select
                className="g_input width_2x"
                value={office}
                onChange={(event) => setOffice(event.target.value)}
              >
                {legacyPushResultOffices.map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </PopupRow>
            <PopupRow title="수신대상" required>
              <select
                id="l_inp_where_pmsg_recv_target_cd"
                className="g_input width_2x"
                value={target}
                onChange={(event) => setTarget(event.target.value)}
              >
                <option>고객</option>
                <option>촬영스케줄</option>
                <option>계약</option>
              </select>
            </PopupRow>
            <PopupRow title="조건(일자)" required>
              <select
                id="l_inp_pmsg_auto_send_select_cd"
                className="g_input width_2x"
                value={dateType}
                onChange={(event) => setDateType(event.target.value)}
              >
                {dateOptions.map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
              <input
                id="l_inp_pmsg_auto_send_period_value"
                className="g_input width_half"
                value={period}
                inputMode="numeric"
                onChange={(event) =>
                  setPeriod(event.target.value.replace(/[^0-9]/g, ""))
                }
              />
              <select
                className="g_input width_half"
                value={periodUnit}
                onChange={(event) => setPeriodUnit(event.target.value)}
              >
                {["일", "주", "년"].map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
              <select
                className="g_input width_half"
                value={periodOrder}
                onChange={(event) => setPeriodOrder(event.target.value)}
              >
                {["전", "후"].map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
              <span className="g_text comment l_comment">
                * &quot;촬영일&quot;은 수신대상 &quot;촬영스케줄&quot;,
                &quot;계약일&quot;은 수신대상 &quot;계약&quot;인 경우에만 선택
                가능
              </span>
            </PopupRow>
            {conditions.map((condition, index) => (
              <ConditionRow
                key={index}
                number={index + 1}
                condition={condition}
                onChange={(value) => updateCondition(index, value)}
                disabledNone={index === 0}
              />
            ))}
            <section className="g_row legacy-m06-content-row">
              <div className="g_row vertical_layout align_left">
                <div className="g_input_wrap">
                  <div className="legacy-m06-content-label">
                    <label className="g_label title center">내용</label>
                    <br />
                    <label className="g_input checkbox_wrap">
                      <input
                        id="l_chk_ad_yn_cd"
                        className="g_input checkbox"
                        type="checkbox"
                        checked={advertising}
                        onChange={(event) =>
                          updateAdvertising(event.target.checked)
                        }
                      />
                      광고
                    </label>
                  </div>
                  <textarea
                    ref={textarea}
                    id="l_inp_pmsg_contents"
                    className="g_input g_input_textarea width_2x"
                    maxLength={2000}
                    value={content}
                    onChange={(event) => setContent(event.target.value)}
                  />
                  &nbsp;
                  <div id="l_pmsg_contents_expand_wrap">
                    <button
                      className="g_btn size_1 l_btn_pmsg_code_view"
                      type="button"
                      onClick={() => setCodeOpen((value) => !value)}
                    >
                      코드 선택
                    </button>
                    {codeOpen && (
                      <div
                        id="l_pmsg_code_btn_wrap"
                        className="g_card g_card_modal"
                      >
                        {mockCodes.map((code) => (
                          <button
                            className="g_btn size_2 l_pmsg_code_btn"
                            type="button"
                            key={code}
                            onClick={() => addCode(code)}
                          >
                            {code}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className="g_input_wrap"
                  id="l_inp_pmsg_contents_func_wrap"
                >
                  <label className="g_label no_style" />
                  <span id="l_pmsg_type">{messageType}</span>&nbsp;
                  <span id="l_pmsg_contents_cnt">{byteCount}</span>&nbsp;/&nbsp;
                  <span id="l_pmsg_contents_cnt_max">
                    {messageType === "SMS" ? 90 : 2000}
                  </span>
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <label className="g_input checkbox_wrap">
                    <input
                      id="l_chk_mms_image"
                      className="g_input checkbox"
                      type="checkbox"
                      checked={isMms}
                      onChange={(event) => setIsMms(event.target.checked)}
                    />
                    이미지 첨부(MMS)
                  </label>
                </div>
                {isMms && (
                  <div className="g_input_wrap">
                    <label className="g_label no_style" />
                    <div id="l_mms_image_wrap" className="g_card g_card_modal">
                      <div className="g_row vertical_layout">
                        <div className="g_row">
                          {[1, 2, 3].map((item) => (
                            <div
                              className="g_thumb g_img_wrap l_mms_image_border"
                              key={item}
                            >
                              Mock
                            </div>
                          ))}
                        </div>
                        <br />
                        <div className="g_row vertical_layout align_left">
                          <div className="g_text comment">
                            *이미지 업로드는 한 번에 최대 3장까지 다중 선택 가능
                            <br />
                            *큰 이미지는 핸드폰으로 작게 변환됨
                          </div>
                          <br />
                          <div>
                            <button
                              className="g_btn upload"
                              type="button"
                              onClick={() =>
                                setNotice(
                                  "Mock MMS 이미지 선택 결과만 처리합니다.",
                                )
                              }
                            >
                              이미지 선택
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
            <PopupRow title="수신자" required>
              <label className="g_input radiobox_wrap">
                <input
                  className="g_input radiobox"
                  type="radio"
                  name="recv_cust_target_cd"
                  checked={receiver === "엄마 또는 아빠"}
                  onChange={() => setReceiver("엄마 또는 아빠")}
                />
                엄마 또는 아빠
              </label>
              <label className="g_input radiobox_wrap">
                <input
                  className="g_input radiobox"
                  type="radio"
                  name="recv_cust_target_cd"
                  checked={receiver === "엄마와 아빠"}
                  onChange={() => setReceiver("엄마와 아빠")}
                />
                엄마와 아빠
              </label>
            </PopupRow>
            <PopupRow title="발송번호" required>
              <select
                id="l_inp_where_send_pno"
                className="g_input width_2x"
                value={calling}
                onChange={(event) => setCalling(event.target.value)}
              >
                <option>010-0000-0001</option>
                <option>010-0000-0002</option>
              </select>
            </PopupRow>
            <br />
            <div className="g_row legacy-m06-save-row">
              <div className="g_input_wrap">
                <label className="g_label no_style" />
                <button className="g_btn g_btn_submit save" type="submit">
                  저장
                </button>
              </div>
            </div>
          </form>
        </section>
      </div>
      {confirmSave && (
        <div className="legacy-m06-modal" role="dialog" aria-modal="true">
          <section>
            <p>Mock 자동 발송 조건을 저장하시겠습니까?</p>
            <button className="g_btn save" type="button" onClick={completeSave}>
              확인
            </button>
            <button
              className="g_btn"
              type="button"
              onClick={() => setConfirmSave(false)}
            >
              취소
            </button>
          </section>
        </div>
      )}
      {notice && (
        <div className="m06-popup-notice">
          {notice}
          <button
            type="button"
            onClick={() => setNotice(null)}
            aria-label="안내 닫기"
          >
            ×
          </button>
        </div>
      )}
    </main>
  );
}

function PopupRow({
  title,
  required,
  children,
}: {
  title: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="g_row">
      <div className={`g_input_wrap${required ? " required" : ""}`}>
        <label className="g_label title center">{title}</label>
        {children}
      </div>
    </div>
  );
}
function ConditionRow({
  number,
  condition,
  onChange,
  disabledNone,
}: {
  number: number;
  condition: { mode: ConditionMode; value: string };
  onChange: (value: Partial<{ mode: ConditionMode; value: string }>) => void;
  disabledNone: boolean;
}) {
  const active = condition.mode !== "none";
  return (
    <div className="g_row l_inp_where_condition_wrap">
      <div className="g_input_wrap">
        <label className="g_label title center l_inp_condition_title">
          조건{number}
        </label>
        {(
          [
            ["none", "없음"],
            ["include", "포함"],
            ["exclude", "제외"],
          ] as const
        ).map(([value, label]) => (
          <label className="g_input radiobox_wrap" key={value}>
            <input
              className="g_input radiobox l_inp_pmsg_auto_send_cond_include_yn_cd"
              type="radio"
              name={`pmsg_auto_send_cond_${number}_include_yn_cd`}
              disabled={disabledNone && value === "none"}
              checked={condition.mode === value}
              onChange={() => onChange({ mode: value })}
            />
            {label}
          </label>
        ))}
        {active && (
          <div className="g_input_wrap l_inp_where_condition_value_wrap">
            <select
              className="g_input width_2x l_inp_pmsg_auto_send_cond_divi_cd"
              value={condition.value}
              onChange={(event) => onChange({ value: event.target.value })}
            >
              {conditionOptions.map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
            &nbsp;
            {condition.value && (
              <select className="g_input width_2x l_inp_pmsg_auto_send_cond_divi_value">
                <option>Mock 조건 값</option>
                <option>Mock 조건 값 02</option>
              </select>
            )}
            <span className="g_text comment l_comment">
              {condition.value ? `선택한 ${condition.value} Mock 조건` : ""}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
